import { useEffect, useRef, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import {
    ArrowLeft,
    Image as ImageIcon,
    Sparkles,
    Wand2,
    Download,
    RefreshCw,
    X,
    Loader2,
    AlertCircle,
    Coins,
    Lock,
} from "lucide-react";

const POLL_MS = 4000;

function loadPaypalSdk(clientId, currency = "USD") {
    return new Promise((resolve, reject) => {
        if (window.paypal) return resolve(window.paypal);
        const existing = document.getElementById("paypal-sdk");
        if (existing) {
            existing.addEventListener("load", () => resolve(window.paypal));
            existing.addEventListener("error", reject);
            return;
        }
        const s = document.createElement("script");
        s.id = "paypal-sdk";
        s.src = `https://www.paypal.com/sdk/js?client-id=${encodeURIComponent(clientId)}&currency=${currency}&intent=capture&disable-funding=credit,paylater`;
        s.async = true;
        s.onload = () => resolve(window.paypal);
        s.onerror = reject;
        document.body.appendChild(s);
    });
}

function PaypalButton({ config, quantity, onSuccess, onError }) {
    const ref = useRef(null);
    const renderedRef = useRef(false);
    const qtyRef = useRef(quantity);
    useEffect(() => { qtyRef.current = quantity; }, [quantity]);

    useEffect(() => {
        let cancelled = false;
        if (!config?.client_id) return;
        loadPaypalSdk(config.client_id, config.currency).then((paypal) => {
            if (cancelled || !ref.current || renderedRef.current) return;
            renderedRef.current = true;
            paypal.Buttons({
                style: { layout: "vertical", color: "blue", shape: "rect", label: "pay", height: 48 },
                createOrder: async () => {
                    const { data } = await api.post("/payments/i2v/order", { quantity: qtyRef.current });
                    return data.order_id;
                },
                onApprove: async (data) => {
                    try {
                        const { data: cap } = await api.post(`/payments/i2v/capture/${data.orderID}`);
                        onSuccess(cap);
                    } catch (e) {
                        onError(formatApiError(e.response?.data?.detail) || "Capture failed");
                    }
                },
                onError: (err) => onError(err?.message || "PayPal error"),
            }).render(ref.current);
        }).catch((e) => onError(e?.message || "Failed to load PayPal"));
        return () => { cancelled = true; };
    }, [config, onSuccess, onError]);

    return <div ref={ref} data-testid="paypal-button-container" />;
}

export default function TryVideoCreator() {
    const { user, loading: authLoading } = useAuth();
    const inputRef = useRef(null);
    const [file, setFile] = useState(null);
    const [previewUrl, setPreviewUrl] = useState(null);
    const [prompt, setPrompt] = useState("");
    const [duration, setDuration] = useState("5");
    const [submitting, setSubmitting] = useState(false);
    const [job, setJob] = useState(null);
    const [error, setError] = useState("");
    const [dragOver, setDragOver] = useState(false);

    const [credits, setCredits] = useState(null);
    const [payConfig, setPayConfig] = useState(null);
    const [payNotice, setPayNotice] = useState("");
    const [buyQty, setBuyQty] = useState(1);

    // Load preview URL
    useEffect(() => {
        if (!file) { setPreviewUrl(null); return; }
        const url = URL.createObjectURL(file);
        setPreviewUrl(url);
        return () => URL.revokeObjectURL(url);
    }, [file]);

    // Load credit balance + paypal config
    const loadBalance = useCallback(async () => {
        if (!user || user === false) return;
        try {
            const { data } = await api.get("/payments/i2v/balance");
            setCredits(data.credits);
        } catch {}
    }, [user]);

    useEffect(() => {
        loadBalance();
        api.get("/payments/i2v/config")
            .then((r) => setPayConfig(r.data))
            .catch(() => setPayConfig(null));
    }, [loadBalance]);

    // Poll the job
    useEffect(() => {
        if (!job?.id) return;
        if (job.status === "COMPLETED" || job.status === "FAILED") return;
        const t = setTimeout(async () => {
            try {
                const { data } = await api.get(`/i2v/jobs/${job.id}`);
                setJob(data);
            } catch {
                setJob((j) => ({ ...j, _ticks: (j._ticks || 0) + 1 }));
            }
        }, POLL_MS);
        return () => clearTimeout(t);
    }, [job]);

    const onPickFile = (f) => {
        if (!f) return;
        if (!f.type.startsWith("image/")) return setError("Please pick an image file");
        if (f.size > 10 * 1024 * 1024) return setError("Image must be under 10MB");
        setError("");
        setFile(f);
    };

    const onSubmit = async (e) => {
        e.preventDefault();
        setError("");
        if (!file) return setError("Please select an image");
        if (credits == null || credits < 1) return setError("Please purchase a credit first");
        const form = new FormData();
        form.append("file", file);
        form.append("prompt", prompt);
        form.append("duration", duration);
        setSubmitting(true);
        setJob(null);
        try {
            const { data } = await api.post("/i2v/jobs", form, { headers: { "Content-Type": "multipart/form-data" } });
            setJob(data);
            loadBalance();
        } catch (e2) {
            setError(formatApiError(e2.response?.data?.detail) || e2.message);
        } finally {
            setSubmitting(false);
        }
    };

    const reset = () => { setJob(null); setError(""); };

    const statusLabel = (s) => ({ IN_QUEUE: "Queued", IN_PROGRESS: "Generating", COMPLETED: "Done!", FAILED: "Failed" }[s] || s);
    const inProgress = job && (job.status === "IN_QUEUE" || job.status === "IN_PROGRESS");
    const isLoggedIn = user && user !== false;

    // ----- Render -----
    const unitPrice = parseFloat(payConfig?.price_usd || "0.40");
    const totalPrice = (unitPrice * buyQty).toFixed(2);

    const renderPaywall = () => (
        <div data-testid="i2v-paywall" className="brutal-card p-6 bg-vibrantyellow">
            <div className="flex items-center gap-2 mb-3">
                <Lock className="w-5 h-5 text-ink" strokeWidth={2.5} />
                <span className="font-heading font-bold text-xs uppercase tracking-widest text-ink">
                    {credits === 0 ? "0 credits left" : "Buy credits"}
                </span>
            </div>
            <h3 className="font-heading font-black text-2xl text-ink tracking-tighter">
                <span className="bg-vibrantorange text-white px-2 -rotate-1 inline-block">${payConfig?.price_usd || "0.40"}</span> per video
            </h3>
            <p className="font-body text-sm text-ink/70 mt-1 mb-4">
                1 credit = 1 image-to-video generation.
            </p>

            {/* Quantity selector */}
            <div className="mb-4" data-testid="i2v-qty-selector">
                <div className="flex items-center justify-between mb-2">
                    <span className="font-heading font-bold text-[10px] uppercase tracking-widest text-ink">How many credits?</span>
                    <span data-testid="i2v-qty-total" className="font-heading font-black text-lg text-ink">${totalPrice}</span>
                </div>
                <div className="flex items-stretch gap-2">
                    <button
                        type="button"
                        onClick={() => setBuyQty((q) => Math.max(1, q - 1))}
                        data-testid="i2v-qty-decrease"
                        className="bg-white border-[3px] border-ink rounded-xl w-11 font-heading font-black text-xl shadow-brutal hover:-translate-y-0.5 active:translate-y-0 active:shadow-none transition-all"
                    >
                        −
                    </button>
                    <input
                        type="number"
                        min={1}
                        max={100}
                        value={buyQty}
                        onChange={(e) => {
                            const v = parseInt(e.target.value, 10);
                            if (Number.isNaN(v)) return setBuyQty(1);
                            setBuyQty(Math.max(1, Math.min(100, v)));
                        }}
                        data-testid="i2v-qty-input"
                        className="flex-1 bg-white border-[3px] border-ink rounded-xl font-heading font-black text-xl text-ink text-center shadow-brutal focus:outline-none focus:shadow-brutal-orange focus:-translate-y-0.5 transition-all"
                    />
                    <button
                        type="button"
                        onClick={() => setBuyQty((q) => Math.min(100, q + 1))}
                        data-testid="i2v-qty-increase"
                        className="bg-white border-[3px] border-ink rounded-xl w-11 font-heading font-black text-xl shadow-brutal hover:-translate-y-0.5 active:translate-y-0 active:shadow-none transition-all"
                    >
                        +
                    </button>
                </div>
                <div className="flex gap-1.5 mt-2" data-testid="i2v-qty-presets">
                    {[1, 5, 10, 25].map((p) => (
                        <button
                            key={p}
                            type="button"
                            onClick={() => setBuyQty(p)}
                            data-testid={`i2v-qty-preset-${p}`}
                            className={`flex-1 font-heading font-bold text-xs uppercase tracking-widest border-[2px] border-ink rounded-lg py-1.5 transition-all ${buyQty === p ? "bg-ink text-white" : "bg-white text-ink hover:bg-cream"}`}
                        >
                            {p}
                        </button>
                    ))}
                </div>
            </div>

            {payConfig?.client_id ? (
                <PaypalButton
                    config={payConfig}
                    quantity={buyQty}
                    onSuccess={(res) => {
                        setPayNotice(`Payment received. +${res.credits_added || buyQty} credits. Ready to generate.`);
                        loadBalance();
                    }}
                    onError={(msg) => setPayNotice(`Payment error: ${msg}`)}
                />
            ) : (
                <div className="font-body text-xs text-ink/60">Payment not configured yet.</div>
            )}
            {payConfig?.mode === "sandbox" && (
                <p className="mt-2 font-body text-[10px] text-ink/50">PayPal sandbox mode — use a sandbox buyer account.</p>
            )}
            <Link
                to="/buy-credits"
                data-testid="i2v-pay-crypto-link"
                className="mt-2 inline-flex items-center gap-1 font-heading font-bold text-xs uppercase tracking-widest text-ink/60 hover:text-vibrantorange"
            >
                Pay with crypto instead →
            </Link>
            {payNotice && (
                <p data-testid="i2v-pay-notice" className="mt-2 font-body font-semibold text-sm text-ink">
                    {payNotice}
                </p>
            )}
        </div>
    );

    return (
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 lg:py-12">
            <Link to="/" data-testid="try-video-back-link" className="inline-flex items-center gap-1.5 font-heading font-bold text-xs uppercase tracking-widest text-ink/60 hover:text-vibrantorange transition-colors mb-3">
                <ArrowLeft className="w-3.5 h-3.5" strokeWidth={2.5} />
                Back to Endfb
            </Link>

            <div className="flex items-start gap-3 mb-6">
                <div className="w-12 h-12 sm:w-14 sm:h-14 bg-vibrantpink border-[3px] border-ink rounded-2xl flex items-center justify-center shadow-brutal-lg flex-shrink-0">
                    <Wand2 className="w-6 h-6 sm:w-7 sm:h-7 text-white" strokeWidth={2.5} />
                </div>
                <div className="flex-1">
                    <h1 className="font-heading font-black text-3xl sm:text-5xl tracking-tighter text-ink leading-tight">
                        Image <span className="text-vibrantorange">→</span> Video
                    </h1>
                    <p className="font-body text-ink/70 mt-1">
                        Drop an image, add a motion prompt, get a short cinematic clip.
                    </p>
                </div>
                {isLoggedIn && credits != null && (
                    <div data-testid="i2v-credits-chip" className="hidden sm:flex items-center gap-1.5 bg-vibrantyellow border-[3px] border-ink rounded-xl px-3 py-2 shadow-brutal flex-shrink-0">
                        <Coins className="w-4 h-4 text-ink" strokeWidth={2.5} />
                        <span className="font-heading font-bold text-sm text-ink">{credits} credit{credits === 1 ? "" : "s"}</span>
                    </div>
                )}
            </div>

            {/* Gate: must be logged in */}
            {authLoading ? (
                <div className="font-heading font-bold text-ink/60">Loading...</div>
            ) : !isLoggedIn ? (
                <div data-testid="i2v-login-gate" className="brutal-card p-8 bg-white max-w-lg">
                    <div className="w-12 h-12 bg-vibrantorange border-[3px] border-ink rounded-2xl flex items-center justify-center shadow-brutal mb-4">
                        <Lock className="w-6 h-6 text-white" strokeWidth={2.5} />
                    </div>
                    <h2 className="font-heading font-black text-3xl text-ink tracking-tighter mb-2">Log in to continue</h2>
                    <p className="font-body text-ink/70 mb-5">
                        You need an account to purchase a video credit and generate clips.
                    </p>
                    <div className="flex flex-wrap gap-3">
                        <Link to="/login" className="brutal-btn-primary">Log in</Link>
                        <Link to="/register" className="brutal-btn-secondary">Create account</Link>
                    </div>
                </div>
            ) : (
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8">
                    {/* Left: form */}
                    <form onSubmit={onSubmit} className="lg:col-span-6 space-y-5" data-testid="i2v-form">
                        <div
                            data-testid="i2v-dropzone"
                            onClick={() => inputRef.current?.click()}
                            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                            onDragLeave={() => setDragOver(false)}
                            onDrop={(e) => { e.preventDefault(); setDragOver(false); onPickFile(e.dataTransfer.files?.[0]); }}
                            className={`cursor-pointer border-[4px] border-dashed border-ink rounded-2xl p-6 sm:p-8 text-center transition-all ${dragOver ? "bg-vibrantyellow" : file ? "bg-cream" : "bg-white"} hover:bg-vibrantyellow`}
                        >
                            <input ref={inputRef} type="file" accept="image/*" className="hidden" onChange={(e) => onPickFile(e.target.files?.[0])} data-testid="i2v-file-input" />
                            {previewUrl ? (
                                <div className="flex flex-col items-center gap-3">
                                    <img src={previewUrl} alt="Preview" className="max-h-64 rounded-xl border-[3px] border-ink shadow-brutal" data-testid="i2v-image-preview" />
                                    <div className="flex items-center gap-2">
                                        <span className="font-heading font-bold text-sm text-ink truncate max-w-[200px]">{file.name}</span>
                                        <button type="button" onClick={(e) => { e.stopPropagation(); setFile(null); }} className="w-8 h-8 bg-white border-[3px] border-ink rounded-lg flex items-center justify-center shadow-brutal hover:-translate-y-0.5 transition-transform" data-testid="i2v-clear-image">
                                            <X className="w-3.5 h-3.5 text-ink" strokeWidth={3} />
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                <>
                                    <ImageIcon className="w-12 h-12 mx-auto text-ink mb-2" strokeWidth={2.5} />
                                    <div className="font-heading font-black text-xl text-ink">Drop an image</div>
                                    <div className="font-body text-sm text-ink/70 mt-1">PNG / JPG / WEBP · up to 10MB</div>
                                </>
                            )}
                        </div>

                        <div>
                            <label className="font-heading font-bold text-xs uppercase tracking-widest text-ink mb-2 block">Motion Prompt</label>
                            <textarea data-testid="i2v-prompt-input" value={prompt} onChange={(e) => setPrompt(e.target.value)} placeholder="e.g. slow camera push-in, gentle wind, cinematic light" rows={3} maxLength={500} className="brutal-input resize-none" />
                        </div>

                        <div>
                            <label className="font-heading font-bold text-xs uppercase tracking-widest text-ink mb-2 block">Duration</label>
                            <div className="flex gap-2" data-testid="i2v-duration-group">
                                {["5", "10"].map((d) => (
                                    <button key={d} type="button" onClick={() => setDuration(d)} data-testid={`i2v-duration-${d}`} className={`brutal-btn text-sm py-2 px-4 ${duration === d ? "bg-vibrantorange text-white" : "bg-white text-ink"}`}>
                                        {d}s
                                    </button>
                                ))}
                            </div>
                        </div>

                        {error && (
                            <div data-testid="i2v-error" className="flex items-start gap-2 bg-vibrantpink/15 border-[3px] border-vibrantpink text-ink rounded-xl p-3">
                                <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5 text-vibrantpink" strokeWidth={2.5} />
                                <span className="font-body font-semibold text-sm">{error}</span>
                            </div>
                        )}

                        {credits !== null && credits < 1 ? (
                            renderPaywall()
                        ) : (
                            <button type="submit" disabled={submitting || inProgress || !file} className="brutal-btn-primary w-full text-base py-3.5" data-testid="i2v-submit-button">
                                <Sparkles className="w-4 h-4 mr-2" strokeWidth={2.5} />
                                {submitting ? "Submitting..." : inProgress ? "Generating..." : `Generate video (uses 1 credit)`}
                            </button>
                        )}

                        <p className="font-body text-xs text-ink/50 text-center">
                            Powered by fal.ai · Kling Video 1.6 · Generation takes ~1-3 minutes
                        </p>
                    </form>

                    {/* Right: result */}
                    <div className="lg:col-span-6">
                        <div data-testid="i2v-result-panel" className="brutal-card p-5 sm:p-6 bg-white sticky top-24">
                            <h2 className="font-heading font-black text-xl text-ink tracking-tight mb-4 flex items-center gap-2">
                                Result
                                {job && (
                                    <span data-testid="i2v-status" className={`text-xs font-heading font-bold uppercase tracking-widest border-[3px] border-ink rounded-full px-2 py-0.5 ${job.status === "COMPLETED" ? "bg-vibrantyellow text-ink" : job.status === "FAILED" ? "bg-vibrantpink text-white" : "bg-vibrantblue text-white"}`}>
                                        {statusLabel(job.status)}
                                    </span>
                                )}
                            </h2>

                            {!job ? (
                                <div data-testid="i2v-result-empty" className="aspect-video bg-cream border-[3px] border-dashed border-ink rounded-xl flex items-center justify-center">
                                    <div className="text-center px-6">
                                        <Wand2 className="w-10 h-10 mx-auto text-ink/40 mb-2" strokeWidth={2.5} />
                                        <p className="font-heading font-bold text-sm uppercase tracking-widest text-ink/50">Your video will appear here</p>
                                    </div>
                                </div>
                            ) : job.status === "COMPLETED" && job.video_url ? (
                                <div className="space-y-3">
                                    <div className="aspect-video bg-ink border-[3px] border-ink rounded-xl overflow-hidden shadow-brutal">
                                        <video data-testid="i2v-result-video" src={job.video_url} controls autoPlay loop playsInline className="w-full h-full" />
                                    </div>
                                    <div className="flex flex-wrap gap-2">
                                        <a href={job.video_url} target="_blank" rel="noopener noreferrer" data-testid="i2v-download-link" className="brutal-btn-secondary text-xs py-2 px-3">
                                            <Download className="w-3.5 h-3.5 mr-1.5" strokeWidth={2.5} />
                                            Open video
                                        </a>
                                        <button type="button" onClick={reset} data-testid="i2v-reset-button" className="brutal-btn-white text-xs py-2 px-3">
                                            <RefreshCw className="w-3.5 h-3.5 mr-1.5" strokeWidth={2.5} />
                                            Start over
                                        </button>
                                    </div>
                                </div>
                            ) : job.status === "FAILED" ? (
                                <div data-testid="i2v-result-failed" className="bg-vibrantpink/15 border-[3px] border-vibrantpink rounded-xl p-4">
                                    <div className="flex items-start gap-2">
                                        <AlertCircle className="w-5 h-5 mt-0.5 text-vibrantpink flex-shrink-0" strokeWidth={2.5} />
                                        <div>
                                            <div className="font-heading font-bold text-ink mb-1">Generation failed</div>
                                            <p className="font-body text-sm text-ink/80 break-words">{job.error || "Unknown error"}</p>
                                        </div>
                                    </div>
                                    <button type="button" onClick={reset} className="brutal-btn-white text-xs py-2 px-3 mt-3" data-testid="i2v-retry-button">
                                        <RefreshCw className="w-3.5 h-3.5 mr-1.5" strokeWidth={2.5} />
                                        Try again
                                    </button>
                                </div>
                            ) : (
                                <div data-testid="i2v-result-loading" className="aspect-video bg-cream border-[3px] border-ink rounded-xl flex items-center justify-center">
                                    <div className="text-center px-6">
                                        <Loader2 className="w-10 h-10 mx-auto text-vibrantorange mb-3 animate-spin" strokeWidth={2.5} />
                                        <p className="font-heading font-black text-lg text-ink">{statusLabel(job.status)}...</p>
                                        <p className="font-body text-xs text-ink/60 mt-1">Hang tight — Kling is rendering your clip.</p>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
